import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { bulkImportProducts, getJobProgress, getMarketplaces, cancelJob } from '../api';
import { useToast } from './Toast';

interface MarketplaceOption {
  id: number;
  code: string;
  domain: string;
}

type ImportMode = 'barcode' | 'asin';
type ImportPhase = 'idle' | 'ready' | 'importing' | 'paused' | 'completed' | 'cancelled' | 'error';

interface ChunkResult {
  index: number;
  rows: number;
  imported: number;
  jobId: string | null;
  status: 'pending' | 'sending' | 'done' | 'error';
  error?: string;
}

const CHUNK_SIZE = 100;

const csvEscape = (val: string): string => {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return '"' + val.replace(/"/g, '""') + '"';
  }
  return val;
};

const BulkImport: React.FC = () => {
  const { showToast } = useToast();
  const [importMode, setImportMode] = useState<ImportMode>('barcode');
  const [textData, setTextData] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [excelSheets, setExcelSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [excelWorkbook, setExcelWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [excelFileName, setExcelFileName] = useState('');

  const [marketplaces, setMarketplaces] = useState<MarketplaceOption[]>([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState('GB');

  // Chunked import state
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [chunks, setChunks] = useState<string[]>([]);
  const [chunkResults, setChunkResults] = useState<ChunkResult[]>([]);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [, setCsvHeader] = useState('');
  const startTimeRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const pauseRef = useRef(false);
  const cancelRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keepa job polling for post-import matching
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);
  const [jobCancelling, setJobCancelling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    getMarketplaces().then(res => {
      setMarketplaces(res.data);
      try {
        const saved = JSON.parse(localStorage.getItem('asin-tracker-settings') || '{}');
        if (saved.defaultMarketplace) setSelectedMarketplace(saved.defaultMarketplace);
      } catch {}
    }).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Elapsed time ticker
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const t0 = Date.now();
    startTimeRef.current = t0;
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - t0) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // Job polling for matching progress
  const stopJobPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startJobPolling = useCallback((jobId: string) => {
    stopJobPolling();
    setActiveJobId(jobId);
    const poll = async () => {
      try {
        const res = await getJobProgress(jobId);
        setJobProgress(res.data);
        if (['completed', 'failed', 'cancelled'].includes(res.data.status)) {
          stopJobPolling();
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
  }, [stopJobPolling]);

  // Column maps
  const barcodeColMap = [
    { header: 'internal_sku', aliases: ['productcode', 'sku', 'internal_sku', 'product_code', 'item_code', 'code'] },
    { header: 'title', aliases: ['name', 'title', 'product_name', 'description', 'product_title'] },
    { header: 'ean', aliases: ['barcode', 'upc', 'ean', 'ean/upc', 'upc/ean', 'gtin'] },
    { header: 'price', aliases: ['price', 'cost', 'unit_price', 'selling_price', 'ffs price'] },
  ];

  const asinColMap = [
    { header: 'internal_sku', aliases: ['productcode', 'sku', 'internal_sku', 'product_code', 'item_code', 'code'] },
    { header: 'title', aliases: ['name', 'title', 'product_name', 'description', 'product_title'] },
    { header: 'asin', aliases: ['asin'] },
    { header: 'price', aliases: ['price', 'cost', 'unit_price', 'selling_price', 'ffs price'] },
  ];

  // Extract data from Excel sheet
  const extractSheetData = useCallback((wb: XLSX.WorkBook, sheetName: string, mode: ImportMode) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (rows.length < 2) { setError('Sheet has no data rows'); return; }

    const headers = (rows[0] as any[]).map((h: any) => String(h || '').toLowerCase().trim());
    const colMap = mode === 'asin' ? asinColMap : barcodeColMap;
    const indices: Record<string, number> = {};
    for (const col of colMap) {
      const idx = headers.findIndex(h => col.aliases.includes(h));
      if (idx >= 0) indices[col.header] = idx;
    }

    const keyCol = mode === 'asin' ? 'asin' : 'ean';
    if (indices[keyCol] === undefined) {
      setError(`No ${mode === 'asin' ? 'ASIN' : 'Barcode/EAN'} column found in sheet "${sheetName}".`);
      return;
    }

    const csvHeaders = Object.keys(indices);
    const csvLines: string[] = [csvHeaders.join(',')];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i] as any[];
      const keyVal = String(row[indices[keyCol]] || '').trim();
      if (!keyVal || keyVal === 'undefined' || keyVal === 'null') continue;
      const parts = csvHeaders.map(h => {
        if (h === keyCol) return keyVal;
        const idx = indices[h];
        if (idx === undefined) return '';
        const val = String(row[idx] || '').trim();
        if (h === 'price') return val && !isNaN(Number(val)) ? val : '';
        return csvEscape(val || (h === 'internal_sku' || h === 'title' ? keyVal : ''));
      });
      csvLines.push(parts.join(','));
    }

    if (csvLines.length <= 1) { setError(`No valid data found in sheet "${sheetName}"`); return; }
    setTextData(csvLines.join('\n'));
    setError('');
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    resetImport();

    if (/\.(xlsx?|xlsm)$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          setExcelWorkbook(wb);
          setExcelFileName(file.name);
          setExcelSheets(wb.SheetNames);
          setSelectedSheet(wb.SheetNames[0]);
          extractSheetData(wb, wb.SheetNames[0], importMode);
        } catch { setError('Failed to read Excel file.'); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setExcelWorkbook(null); setExcelSheets([]); setExcelFileName('');
      const reader = new FileReader();
      reader.onload = (event) => { setTextData(event.target?.result as string); setError(''); };
      reader.readAsText(file);
    }
  };

  const handleSheetChange = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (excelWorkbook) extractSheetData(excelWorkbook, sheetName, importMode);
  };

  const handleModeChange = (mode: ImportMode) => {
    setImportMode(mode);
    if (excelWorkbook && selectedSheet) {
      extractSheetData(excelWorkbook, selectedSheet, mode);
    } else { setTextData(''); }
    setError('');
    resetImport();
  };

  // Parse text into chunks
  const prepareChunks = (): boolean => {
    if (!textData.trim()) { setError('No data to import'); return false; }
    const lines = textData.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) { setError('Data must have a header row and at least one data row'); return false; }

    const header = lines[0];
    const dataRows = lines.slice(1);

    setCsvHeader(header);
    setTotalRows(dataRows.length);

    const chunkArr: string[] = [];
    for (let i = 0; i < dataRows.length; i += CHUNK_SIZE) {
      const slice = dataRows.slice(i, i + CHUNK_SIZE);
      chunkArr.push([header, ...slice].join('\n'));
    }

    setChunks(chunkArr);
    setChunkResults(chunkArr.map((_, idx) => ({
      index: idx,
      rows: Math.min(CHUNK_SIZE, dataRows.length - idx * CHUNK_SIZE),
      imported: 0,
      jobId: null,
      status: 'pending',
    })));
    setCurrentChunk(0);
    return true;
  };

  const resetImport = () => {
    setPhase('idle');
    setChunks([]);
    setChunkResults([]);
    setCurrentChunk(0);
    setTotalRows(0);
    setCsvHeader('');
    startTimeRef.current = null;
    setElapsed(0);
    setActiveJobId(null);
    setJobProgress(null);
    setJobCancelling(false);
    pauseRef.current = false;
    cancelRef.current = false;
    stopTimer();
    stopJobPolling();
  };

  // Run the chunked import
  const runImport = useCallback(async (fromChunk: number) => {
    setPhase('importing');
    pauseRef.current = false;
    cancelRef.current = false;
    if (fromChunk === 0) startTimer();

    const marketplace = selectedMarketplace;
    let finalJobId: string | null = null;

    for (let i = fromChunk; i < chunks.length; i++) {
      if (cancelRef.current) {
        setPhase('cancelled');
        stopTimer();
        showToast(`Import cancelled after ${i} of ${chunks.length} batches`, 'warning');
        return;
      }
      if (pauseRef.current) {
        setPhase('paused');
        stopTimer();
        showToast('Import paused', 'info');
        return;
      }

      setCurrentChunk(i);
      setChunkResults(prev => prev.map((cr, idx) =>
        idx === i ? { ...cr, status: 'sending' } : cr
      ));

      try {
        const res = await bulkImportProducts(chunks[i], 'csv', marketplace);
        const batchJobId = res.data.job_id || null;
        finalJobId = batchJobId;
        setChunkResults(prev => prev.map((cr, idx) =>
          idx === i ? { ...cr, status: 'done', imported: res.data.imported_count || cr.rows, jobId: batchJobId } : cr
        ));
      } catch (err: any) {
        const detail = err.response?.data?.detail;
        const errMsg = typeof detail === 'string' ? detail : `Batch ${i + 1} failed`;
        setChunkResults(prev => prev.map((cr, idx) =>
          idx === i ? { ...cr, status: 'error', error: errMsg } : cr
        ));
      }
    }

    setPhase('completed');
    stopTimer();
    showToast('Import completed', 'success');

    if (finalJobId) startJobPolling(finalJobId);
  }, [chunks, importMode, selectedMarketplace, startTimer, stopTimer, showToast, startJobPolling]);

  const handleStartImport = () => {
    if (!prepareChunks()) return;
    setTimeout(() => {
      setPhase('importing');
    }, 0);
  };

  // Trigger runImport when phase transitions to 'importing' and chunks are ready
  useEffect(() => {
    if (phase === 'importing' && chunks.length > 0) {
      const startFrom = chunkResults.findIndex(cr => cr.status === 'pending' || cr.status === 'error');
      if (startFrom >= 0) {
        runImport(startFrom);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, chunks.length]);

  const handlePause = () => { pauseRef.current = true; };
  const handleResume = () => {
    if (phase === 'paused') {
      startTimer();
      setPhase('importing');
    }
  };
  const handleCancel = () => {
    cancelRef.current = true;
    if (phase === 'paused') {
      setPhase('cancelled');
      stopTimer();
      showToast('Import cancelled', 'warning');
    }
  };
  const handleRetry = () => {
    setChunkResults(prev => prev.map(cr =>
      cr.status === 'error' ? { ...cr, status: 'pending', error: undefined } : cr
    ));
    startTimer();
    setPhase('importing');
  };
  const handleDismiss = () => { resetImport(); };

  const handleStopKeepaJob = async () => {
    if (!activeJobId) return;
    setJobCancelling(true);
    try {
      const res = await cancelJob(activeJobId);
      if (res.data?.detail === 'Job already finished') {
        showToast('Job already finished', 'info');
      } else {
        showToast('Stopping after current batch…', 'info');
      }
    } catch {
      showToast('Job may have already finished', 'info');
    } finally {
      setJobCancelling(false);
    }
  };

  // Computed values
  const previewCount = textData.trim() ? Math.max(0, textData.trim().split('\n').filter(l => l.trim()).length - 1) : 0;
  const completedChunks = chunkResults.filter(cr => cr.status === 'done').length;
  const errorChunks = chunkResults.filter(cr => cr.status === 'error').length;
  const importedRows = chunkResults.reduce((sum, cr) => sum + (cr.status === 'done' ? cr.imported : 0), 0);
  const isActive = phase === 'importing';
  const chunkCount = chunks.length;

  const formatTime = (secs: number): string => {
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m < 60 ? `${m}m ${s}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const speed = elapsed > 0 ? Math.round(importedRows / elapsed * 60) : 0;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Bulk Import Products</h2>
          <p>Upload an Excel (.xlsx) or CSV file with product data</p>
        </div>
      </div>

      {/* Import Mode Tabs */}
      <div className="tab-bar" style={{ marginBottom: 20 }}>
        <button className={`tab-btn ${importMode === 'barcode' ? 'active' : ''}`} onClick={() => handleModeChange('barcode')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: '-2px' }}>
            <path d="M2 4h2v16H2zM6 4h1v16H6zM10 4h2v16h-2zM15 4h1v16h-1zM19 4h3v16h-3z" />
          </svg>
          Import by Barcode
        </button>
        <button className={`tab-btn ${importMode === 'asin' ? 'active' : ''}`} onClick={() => handleModeChange('asin')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: '-2px' }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Import by ASIN
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Import Manager Panel ──────────────────────────────────────── */}
      {phase !== 'idle' && (
        <div className="bi-manager">
          <div className="bi-manager-header">
            <div className="bi-manager-title">
              <div className="bi-manager-title-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="12" y2="12" />
                  <line x1="15" y1="15" x2="12" y2="12" />
                </svg>
              </div>
              Import Progress
            </div>
            <div className="bi-manager-controls">
              <span className={`bi-status bi-status-${phase}`}>
                {phase === 'importing' ? 'Importing…' :
                 phase === 'paused' ? 'Paused' :
                 phase === 'completed' ? 'Completed' :
                 phase === 'cancelled' ? 'Cancelled' :
                 phase === 'error' ? 'Error' : ''}
              </span>
              {phase === 'importing' && (
                <button className="btn btn-secondary btn-small" onClick={handlePause}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                  Pause
                </button>
              )}
              {phase === 'paused' && (
                <>
                  <button className="btn btn-primary btn-small" onClick={handleResume}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    Resume
                  </button>
                  <button className="btn btn-danger btn-small" onClick={handleCancel}>Cancel</button>
                </>
              )}
              {phase === 'importing' && (
                <button className="btn btn-danger btn-small" onClick={handleCancel}>Cancel</button>
              )}
              {(phase === 'completed' || phase === 'cancelled') && errorChunks > 0 && (
                <button className="btn btn-secondary btn-small" onClick={handleRetry}>Retry Failed</button>
              )}
              {(phase === 'completed' || phase === 'cancelled' || phase === 'error') && (
                <button className="btn btn-ghost btn-small" onClick={handleDismiss}>Dismiss</button>
              )}
            </div>
          </div>
          <div className="bi-manager-body">
            {/* Stats overview */}
            <div className="bi-progress-overview">
              <div className="bi-progress-stat">
                <div className="bi-progress-stat-value">{totalRows.toLocaleString()}</div>
                <div className="bi-progress-stat-label">Total Rows</div>
              </div>
              <div className="bi-progress-stat">
                <div className="bi-progress-stat-value accent">{importedRows.toLocaleString()}</div>
                <div className="bi-progress-stat-label">Imported</div>
              </div>
              <div className="bi-progress-stat">
                <div className="bi-progress-stat-value success">{completedChunks}/{chunkCount}</div>
                <div className="bi-progress-stat-label">Batches Done</div>
              </div>
              <div className="bi-progress-stat">
                <div className={`bi-progress-stat-value ${errorChunks > 0 ? 'error' : ''}`}>{errorChunks}</div>
                <div className="bi-progress-stat-label">Failed</div>
              </div>
            </div>

            {/* Chunk progress bar */}
            {chunkCount > 0 && (
              <>
                <div className="bi-chunk-bar">
                  {chunkResults.map((cr, i) => (
                    <div
                      key={i}
                      className={`bi-chunk-seg ${cr.status === 'done' ? 'done' : cr.status === 'sending' ? 'active' : cr.status === 'error' ? 'error' : 'pending'}`}
                      title={`Batch ${i + 1}: ${cr.rows} rows — ${cr.status}${cr.error ? ': ' + cr.error : ''}`}
                    />
                  ))}
                </div>
                <div className="bi-chunk-label">
                  <span>Batch {Math.min(currentChunk + 1, chunkCount)} of {chunkCount} ({CHUNK_SIZE} rows/batch)</span>
                  <span>{Math.round((completedChunks / chunkCount) * 100)}%</span>
                </div>
              </>
            )}

            {/* Errors */}
            {chunkResults.some(cr => cr.error) && (
              <div className="job-errors" style={{ marginBottom: 10 }}>
                <div className="job-errors-header">
                  Batch Errors ({errorChunks}):
                </div>
                <div className="job-errors-list">
                  {chunkResults.filter(cr => cr.error).map(cr => (
                    <div key={cr.index} className="job-error-item">
                      Batch {cr.index + 1}: {cr.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta bar */}
            <div className="bi-meta-bar">
              <span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                Elapsed: {formatTime(elapsed)}
              </span>
              {speed > 0 && (
                <span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                  {speed} rows/min
                </span>
              )}
              {phase === 'importing' && completedChunks < chunkCount && speed > 0 && (
                <span>
                  ~{formatTime(Math.round((totalRows - importedRows) / (speed / 60)))} remaining
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keepa matching job progress (after import completes) */}
      {jobProgress && (
        <div className="card job-progress-card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title">Keepa Matching Progress</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {jobProgress.status && !['completed', 'failed', 'cancelled'].includes(jobProgress.status) && (
                <button
                  className="btn btn-danger btn-small"
                  onClick={handleStopKeepaJob}
                  disabled={jobCancelling}
                  style={{ padding: '2px 10px', fontSize: '11px' }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4, verticalAlign: '-1px' }}>
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                  {jobCancelling ? 'Stopping…' : 'Stop'}
                </button>
              )}
              <span className={`job-status-badge ${
                jobProgress.status === 'completed' ? 'job-status-completed' :
                jobProgress.status === 'failed' ? 'job-status-failed' :
                jobProgress.status === 'cancelled' ? 'job-status-failed' :
                jobProgress.status === 'waiting_for_tokens' ? 'job-status-waiting' :
                'job-status-processing'
              }`}>
                {jobProgress.status === 'waiting_for_tokens' ? 'Waiting for Tokens' :
                 jobProgress.status === 'completed' ? 'Completed' :
                 jobProgress.status === 'failed' ? 'Failed' :
                 jobProgress.status === 'cancelled' ? 'Stopped' : 'Processing'}
              </span>
            </div>
          </div>
          <div className="job-progress-bar-container">
            <div className="job-progress-bar">
              <div
                className={`job-progress-fill ${jobProgress.status === 'waiting_for_tokens' ? 'pulsing' : ''}`}
                style={{ width: `${jobProgress.total > 0 ? Math.round((jobProgress.processed / jobProgress.total) * 100) : 0}%` }}
              />
            </div>
            <div className="job-progress-text">
              <span>{jobProgress.processed} / {jobProgress.total} products</span>
            </div>
          </div>
          {jobProgress.status === 'completed' && (
            <div className="job-completed-msg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>
                Done. {jobProgress.matched > 0 && `${jobProgress.matched} matched.`}
                {jobProgress.enriched > 0 && ` ${jobProgress.enriched} enriched.`}
                {jobProgress.no_match > 0 && ` ${jobProgress.no_match} not found.`}
              </span>
            </div>
          )}
          {jobProgress.status === 'cancelled' && (
            <div className="job-completed-msg" style={{ borderColor: 'var(--warning)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <rect x="9" y="9" width="6" height="6" rx="1" fill="var(--warning)" />
              </svg>
              <span>
                Stopped at {jobProgress.processed} of {jobProgress.total} products.
                {jobProgress.matched > 0 && ` ${jobProgress.matched} matched.`}
                {jobProgress.enriched > 0 && ` ${jobProgress.enriched} enriched.`}
                {' '}Already-processed products keep their data.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Upload Card ────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            {importMode === 'asin' ? 'Upload ASIN Product File' : 'Upload Barcode Product File'}
          </span>
          {previewCount > 0 && (
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {previewCount.toLocaleString()} product{previewCount !== 1 ? 's' : ''} detected
            </span>
          )}
        </div>

        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
          Expected format: <strong style={{ color: 'var(--text-primary)' }}>
            {importMode === 'asin' ? 'Sku, Name, Asin, Price' : 'Productcode, Name, Barcode, Price'}
          </strong> (extra columns are ignored). Upload <strong style={{ color: 'var(--text-primary)' }}>.xlsx</strong> or paste CSV.
        </p>

        <pre style={{
          background: 'var(--bg-input)', border: '1px solid var(--border-primary)',
          borderRadius: '6px', padding: '10px 12px', fontSize: '12px',
          color: 'var(--text-muted)', marginBottom: '14px', overflowX: 'auto', whiteSpace: 'pre',
        }}>
          {importMode === 'asin'
            ? 'Sku,Name,Asin,FFS Price\nN100001,Elex 2 (Xbox One),B098CGFT87,5.4'
            : 'Productcode,Name,Barcode,Price\nN00008,MX vs ATV,9006113008262,8.23'}
        </pre>

        {marketplaces.length > 0 && (
          <div className="form-group" style={{ marginBottom: '14px' }}>
            <label>Marketplace for Keepa Enrichment</label>
            <select
              className="filter-select"
              value={selectedMarketplace}
              onChange={(e) => setSelectedMarketplace(e.target.value)}
              style={{ maxWidth: '250px' }}
              disabled={isActive}
            >
              {marketplaces.map(mp => (
                <option key={mp.code} value={mp.code}>{mp.code} — {mp.domain}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Upload File (.xlsx, .csv)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.xlsm,.csv"
            onChange={handleFileUpload}
            disabled={isActive}
          />
        </div>

        {excelSheets.length > 1 && (
          <div className="form-group">
            <label>Select Sheet from {excelFileName}</label>
            <select
              className="filter-select"
              value={selectedSheet}
              onChange={(e) => handleSheetChange(e.target.value)}
              disabled={isActive}
            >
              {excelSheets.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        {excelWorkbook && previewCount > 0 && (
          <div className="bi-file-info">
            <div className="bi-file-info-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <strong>{excelFileName}</strong>
            </div>
            <div className="bi-file-info-divider" />
            <div className="bi-file-info-item">Sheet: <strong>{selectedSheet}</strong></div>
            <div className="bi-file-info-divider" />
            <div className="bi-file-info-item"><strong>{previewCount.toLocaleString()}</strong> rows</div>
            <div className="bi-file-info-divider" />
            <div className="bi-file-info-item"><strong>{Math.ceil(previewCount / CHUNK_SIZE)}</strong> batches</div>
          </div>
        )}

        <div className="form-group">
          <label>Or Paste CSV Data</label>
          <textarea
            value={textData}
            onChange={(e) => { setTextData(e.target.value); setError(''); if (phase !== 'idle') resetImport(); }}
            placeholder={importMode === 'asin'
              ? 'Sku,Name,Asin,FFS Price\nN100001,Elex 2,B098CGFT87,5.4'
              : 'Productcode,Name,Barcode,Price\nN00008,MX vs ATV,9006113008262,8.23'}
            rows={8}
            style={{ fontFamily: "'JetBrains Mono', 'Courier New', monospace", fontSize: '12px' }}
            disabled={isActive || phase === 'paused'}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={handleStartImport}
            disabled={isActive || phase === 'paused' || !textData.trim()}
          >
            {isActive ? 'Importing...' : `Import ${previewCount > 0 ? previewCount.toLocaleString() + ' ' : ''}Product${previewCount !== 1 ? 's' : ''}`}
          </button>
          {previewCount > CHUNK_SIZE && phase === 'idle' && (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Will be split into {Math.ceil(previewCount / CHUNK_SIZE)} batches of {CHUNK_SIZE}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
